// Apply a saved service template to THIS client (Build P1b + per-client apply).
// Builders live at /templates; here a coordinator picks a template + date range and
// generates this client's recurring shifts (clientOverride targets this client).
import React, { useState, useEffect } from 'react';
import { CalendarClock, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { isoDate } from '../../utils/time';

const fmt = isoDate;

export default function ClientServiceTemplates({ clientId }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState('');
  const [from, setFrom] = useState(fmt(new Date()));
  const [to, setTo] = useState(fmt(new Date(Date.now() + 28 * 86400000)));
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get('/templates').then(r => setTemplates((Array.isArray(r.data) ? r.data : []).filter(t => t.template_type !== 'group')))
      .catch(() => {});
  }, []);

  const run = async (dryRun) => {
    if (!selected) return toast.error('Pick a template');
    if (!from || !to) return toast.error('Set a date range');
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post(`/templates/${selected}/generate`, { fromDate: from, toDate: to, clientOverride: clientId, dryRun });
      if (dryRun) { setPreview(data); }
      else { toast.success(`Generated ${data.created} shift${data.created === 1 ? '' : 's'}${data.skipped ? `, ${data.skipped} skipped` : ''}`); setPreview(null); }
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-slate-100">
        <CalendarClock size={15} className="text-slate-400" aria-hidden />
        <h3 className="text-sm font-semibold text-slate-700">Service templates</h3>
        <span className="ml-auto text-[12px] text-slate-500">Apply a recurring pattern to this client</span>
      </div>
      <div className="p-5 space-y-3">
        {templates.length === 0 ? (
          <p className="text-sm text-slate-500">No individual templates yet. Build one in <a href="/templates" className="text-primary-600 hover:underline">Templates</a>, then apply it here.</p>
        ) : (
          <>
            <select value={selected} aria-label="Service template" onChange={e => { setSelected(e.target.value); setPreview(null); }}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300">
              <option value="">Select a template…</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.item_count ? ` (${t.item_count} service${t.item_count > 1 ? 's' : ''})` : ''}</option>)}
            </select>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label htmlFor={`tpl-from-${clientId}`} className="block text-[13px] font-medium text-slate-700 mb-1">From</label><input id={`tpl-from-${clientId}`} type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" /></div>
              <div><label htmlFor={`tpl-to-${clientId}`} className="block text-[13px] font-medium text-slate-700 mb-1">To</label><input id={`tpl-to-${clientId}`} type="date" value={to} onChange={e => setTo(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" /></div>
            </div>
            {preview && (
              <div className="text-xs bg-slate-50 border border-slate-200 text-slate-700 rounded-lg px-3 py-2" role="status">
                Preview: <b>{preview.created}</b> shift{preview.created === 1 ? '' : 's'} would be created{preview.skipped ? `, ${preview.skipped} already exist` : ''}.
              </div>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={() => run(true)} disabled={busy || !selected} className="btn-secondary">
                {busy ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <Sparkles size={14} className="text-slate-400" aria-hidden />} Preview
              </button>
              <button type="button" onClick={() => run(false)} disabled={busy || !selected} className="btn-primary">
                {busy ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <ArrowRight size={14} aria-hidden />} Generate shifts
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
